import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyMove,
  createInitialBoard,
  formatSquare,
  getLegalMoves,
  getBotMove,
} from '../draughts/engine';
import '../styles/Flappy.css';
import '../styles/Draughts.css';

const TURN_TIMER_OPTIONS = [0, 10, 15, 20, 30];
const BOT_LEVELS = [
  { value: 1, label: 'Beginner', desc: 'Plays randomly' },
  { value: 2, label: 'Intermediate', desc: 'Thinks one step ahead' },
  { value: 3, label: 'Professional', desc: 'Calculates deeply' },
];

function pieceMeta(piece) {
  if (!piece) return null;
  const lower = String(piece).toLowerCase();
  const color = lower === 'w' ? 'white' : 'black';
  const king = piece === 'W' || piece === 'B';
  return { color, king };
}

function formatTimer(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function DraughtsPractice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialLevel = Math.min(3, Math.max(1, Number(searchParams.get('level')) || 1));
  const rawBotName = String(searchParams.get('botName') || '').trim();
  const botName = rawBotName ? rawBotName.slice(0, 24) : 'ObaMaster';
  const [board, setBoard] = useState(() => createInitialBoard());
  const [turn, setTurn] = useState('w');
  const [forcedSquare, setForcedSquare] = useState(null);
  const [alreadyCaptured, setAlreadyCaptured] = useState([]);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [status, setStatus] = useState('Your turn (White).');
  const [winner, setWinner] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [turnTimeoutSeconds, setTurnTimeoutSeconds] = useState(15);
  const [turnStartedAt, setTurnStartedAt] = useState(Date.now());
  const [tick, setTick] = useState(Date.now());
  const [botLevel, setBotLevel] = useState(initialLevel);
  const [whiteTimeouts, setWhiteTimeouts] = useState(0);
  const [blackTimeouts, setBlackTimeouts] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 200);
    return () => window.clearInterval(id);
  }, []);

  const isTimedMode = turnTimeoutSeconds > 0;
  const timeLeftMs = isTimedMode ? Math.max(0, (turnStartedAt + turnTimeoutSeconds * 1000) - tick) : null;

  const legalMoves = useMemo(() => {
    if (winner) return [];
    return getLegalMoves(board, turn, forcedSquare, alreadyCaptured);
  }, [board, turn, forcedSquare, alreadyCaptured, winner]);

  const legalTargets = useMemo(() => {
    if (!selectedSquare) return [];
    return legalMoves.filter((m) => m.from === selectedSquare).map((m) => m.to);
  }, [legalMoves, selectedSquare]);

  useEffect(() => {
    if (winner) return;
    if (legalMoves.length > 0) return;
    const win = turn === 'w' ? `${botName} (Black)` : 'You (White)';
    setWinner(win);
    setStatus(`No legal moves. Winner: ${win}`);
  }, [legalMoves, turn, winner, botName]);

  useEffect(() => {
    if (winner) return;
    if (!isTimedMode) return;
    if (timeLeftMs > 0) return;
    
    if (turn === 'w') {
      const newStrikes = whiteTimeouts + 1;
      if (newStrikes >= 3) {
        setWinner(`${botName} (Black)`);
        setStatus(`Turn timeout (3 strikes). ${botName} wins.`);
      } else {
        setWhiteTimeouts(newStrikes);
        setTurnStartedAt(Date.now());
        setSelectedSquare(null);
        setAlreadyCaptured([]);
        setForcedSquare(null);
        setStatus(`Timeout strike ${newStrikes}/3. Your turn.`);
      }
    } else {
      const newStrikes = blackTimeouts + 1;
      if (newStrikes >= 3) {
        setWinner('You (White)');
        setStatus(`${botName} timed out (3 strikes). You win.`);
      } else {
        setBlackTimeouts(newStrikes);
        setTurnStartedAt(Date.now());
        setStatus(`${botName} timeout strike ${newStrikes}/3. Thinking...`);
      }
    }
  }, [botName, isTimedMode, timeLeftMs, turn, winner, whiteTimeouts, blackTimeouts]);

  useEffect(() => {
    if (winner) return;
    if (turn !== 'b') return;
    if (!legalMoves.length) return;

    const timer = window.setTimeout(() => {
      const pick = getBotMove(board, 'b', forcedSquare, alreadyCaptured, botLevel);
      if (!pick) return;

      const result = applyMove(board, 'b', pick.from, pick.to, forcedSquare, alreadyCaptured);
      if (!result.ok) return;

      setBoard(result.board);
      setTurn(result.nextTurn);
      setForcedSquare(result.mustContinueFrom || null);
      setLastMove(result.move);

      if (result.mustContinueFrom) {
        // Multi-capture continues — keep accumulated captured squares
        setAlreadyCaptured(result.alreadyCaptured || []);
        setStatus(`${botName} continues from ${result.mustContinueFrom}.`);
      } else {
        // Turn is over — reset captured tracking for the next turn
        setAlreadyCaptured([]);
        setTurnStartedAt(Date.now());
        setStatus('Your turn (White).');
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [board, forcedSquare, alreadyCaptured, legalMoves, turn, winner, botLevel, botName]);

  const reset = () => {
    setBoard(createInitialBoard());
    setTurn('w');
    setForcedSquare(null);
    setAlreadyCaptured([]);
    setSelectedSquare(null);
    setStatus('Your turn (White).');
    setWinner(null);
    setLastMove(null);
    setWhiteTimeouts(0);
    setBlackTimeouts(0);
    setTurnStartedAt(Date.now());
  };

  const onSquareClick = (row, col) => {
    if (winner) return;
    if (turn !== 'w') return;

    const clicked = formatSquare(row, col);

    if (!selectedSquare) {
      const piece = board[row]?.[col];
      if (!piece) return;
      if (String(piece).toLowerCase() !== 'w') return;
      if (!legalMoves.some((m) => m.from === clicked)) return;
      setSelectedSquare(clicked);
      return;
    }

    if (clicked === selectedSquare) { setSelectedSquare(null); return; }

    if (!legalTargets.includes(clicked)) {
      const piece = board[row]?.[col];
      if (piece && String(piece).toLowerCase() === 'w' && legalMoves.some((m) => m.from === clicked)) {
        setSelectedSquare(clicked);
      }
      return;
    }

    const result = applyMove(board, 'w', selectedSquare, clicked, forcedSquare, alreadyCaptured);
    if (!result.ok) { setSelectedSquare(null); return; }

    setBoard(result.board);
    setTurn(result.nextTurn);
    setForcedSquare(result.mustContinueFrom || null);
    setSelectedSquare(null);
    setLastMove(result.move);

    if (result.mustContinueFrom) {
      // Multi-capture continues — keep accumulated captured squares
      setAlreadyCaptured(result.alreadyCaptured || []);
      setStatus(`Continue capture from ${result.mustContinueFrom}.`);
    } else {
      // Turn is over — reset captured tracking for the next turn
      setAlreadyCaptured([]);
      setTurnStartedAt(Date.now());
      setStatus(`${botName} thinking...`);
    }
  };

  return (
    <div className="chess-match-page">
      {/* ───── TOP BAR ───── */}
      <header className="cm-topbar">
        <button className="cm-icon-btn" onClick={() => navigate('/draughts/lobby')} title="Lobby">
          <span>←</span>
        </button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">Draughts vs {botName}</span>
          <span className="cm-badge">Lv{botLevel}</span>
        </div>
        <button className="cm-icon-btn" onClick={() => navigate('/dashboard')} title="Dashboard">
          <span>⌂</span>
        </button>
      </header>

      {/* ───── TOP PLAYER BAR (Bot / Black) ───── */}
      <div className={`cm-player-bar${turn === 'b' ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">⚫</span>
          <span className="cm-player-bar__name">{botName} ({BOT_LEVELS.find((l) => l.value === botLevel)?.label})</span>
        </div>
        <div className={`cm-clock${turn === 'b' && !winner ? ' cm-clock--ticking' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div>{turn === 'b' && !winner && isTimedMode ? formatTimer(timeLeftMs) : turnTimeoutSeconds > 0 ? `${turnTimeoutSeconds}s` : 'No time'}</div>
          {blackTimeouts > 0 && <div style={{ fontSize: '11px', color: '#e11d48', marginTop: '2px', fontWeight: 600 }}>Strike {blackTimeouts}/3</div>}
        </div>
      </div>

      {/* ───── BOARD ───── */}
      <div className="dm-board-wrap">
        <div className="draughts-board-wrap">
          <div className="draughts-board-with-labels">
            <span />
            <div className="draughts-board-labels-top">
              {['a','b','c','d','e','f','g','h','i','j'].map(f => <span key={f}>{f}</span>)}
            </div>
            <span />

            <div className="draughts-board-labels-left">
              {Array.from({length:10},(_,i) => <span key={i}>{10-i}</span>)}
            </div>

            <div className="draughts-board">
              {board.map((row, rIdx) => row.map((piece, cIdx) => {
                const square = formatSquare(rIdx, cIdx);
                const isDark = (rIdx + cIdx) % 2 === 0;
                const isSelected = selectedSquare === square;
                const isTarget = legalTargets.includes(square);
                const isLast = lastMove && (lastMove.from === square || lastMove.to === square);
                const meta = pieceMeta(piece);
                const isMovable = !winner && turn === 'w' && meta && meta.color === 'white' && legalMoves.some((m) => m.from === square);
                return (
                  <button
                    key={square}
                    type="button"
                    className={[
                      'draughts-square',
                      isDark ? 'draughts-square--dark' : 'draughts-square--light',
                      isSelected ? 'draughts-square--selected' : '',
                      isTarget ? 'draughts-square--target' : '',
                      isLast ? 'draughts-square--last' : '',
                    ].join(' ')}
                    onClick={() => onSquareClick(rIdx, cIdx)}
                    disabled={!isDark}
                  >
                    {meta && (
                      <span className={[
                        'draughts-piece',
                        `draughts-piece--${meta.color}`,
                        meta.king ? 'draughts-piece--king' : '',
                        isMovable ? 'draughts-piece--movable' : '',
                      ].join(' ')} />
                    )}
                  </button>
                );
              }))}
            </div>

            <div className="draughts-board-labels-right">
              {Array.from({length:10},(_,i) => <span key={i}>{10-i}</span>)}
            </div>

            <span />
            <div className="draughts-board-labels-bottom">
              {['a','b','c','d','e','f','g','h','i','j'].map(f => <span key={f}>{f}</span>)}
            </div>
            <span />
          </div>
        </div>
      </div>

      {/* ───── BOTTOM PLAYER BAR (You / White) ───── */}
      <div className={`cm-player-bar${turn === 'w' ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">⚪</span>
          <span className="cm-player-bar__name">You (White)</span>
        </div>
        <div className={`cm-clock${turn === 'w' && !winner ? ' cm-clock--ticking' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div>{turn === 'w' && !winner && isTimedMode ? formatTimer(timeLeftMs) : turnTimeoutSeconds > 0 ? `${turnTimeoutSeconds}s` : 'No time'}</div>
          {whiteTimeouts > 0 && <div style={{ fontSize: '11px', color: '#e11d48', marginTop: '2px', fontWeight: 600 }}>Strike {whiteTimeouts}/3</div>}
        </div>
      </div>

      {/* ───── RESULT BANNER ───── */}
      {winner && (
        <div className={`cm-result-banner${winner.includes('You') ? ' cm-result-banner--win' : ' cm-result-banner--loss'}`}>
          <span className="cm-result-banner__icon">{winner.includes('You') ? '🏆' : '💔'}</span>
          <span>{winner}</span>
        </div>
      )}

      {/* ───── STATUS LINE ───── */}
      <div className="cm-status-line">
        {status}
        {forcedSquare && ` — Must continue from ${forcedSquare}`}
      </div>

      {/* ───── ACTIONS ───── */}
      <div className="cm-actions">
        <button className="cm-btn cm-btn--primary" onClick={reset}>New Game</button>
        <select
          className="cm-btn cm-btn--ghost"
          style={{ appearance: 'auto' }}
          value={botLevel}
          onChange={(e) => setBotLevel(Number(e.target.value))}
        >
          {BOT_LEVELS.map((lvl) => (
            <option key={lvl.value} value={lvl.value}>{botName}: {lvl.label}</option>
          ))}
        </select>
        <select
          className="cm-btn cm-btn--ghost"
          style={{ appearance: 'auto' }}
          value={turnTimeoutSeconds}
          onChange={(e) => {
            const next = Number(e.target.value);
            setTurnTimeoutSeconds(next);
            setTurnStartedAt(Date.now());
          }}
        >
          {TURN_TIMER_OPTIONS.map((seconds) => (
            <option key={seconds} value={seconds}>Timer: {seconds > 0 ? `${seconds}s` : 'No time'}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
